import { AsyncThunkAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { FormikBag } from 'formik';

import { ReactComponent as CheckmarkIcon } from '../../../../../shared/assets/icons/snackbar-icons/check-mark.svg';
import { ReactComponent as WarningIcon } from '../../../../../shared/assets/icons/snackbar-icons/warning.svg';
import { BasicInfoRequestModel } from '../../../../models/basic-info-request.model';
import { CampaignRequestModel } from '../../../../models/campaign-request.model';
import { CampaignResponseModel } from '../../../../models/campaign-response.model';
import { VehiclePopulationRequestModel } from '../../../../models/vehicle-population-request.model';
import {
  resetBasicInfo,
  setBasicInfoError,
} from '../../../../store/slices/campaigns-basic-info.slice';
import { resetVehiclePopulation } from '../../../../store/slices/campaigns-vehicle-population.slice';
import { createCampaign } from '../../../../thunks/create-campaign.thunk';
import { updateCampaign } from '../../../../thunks/update-campaign.thunk';
import { getBasicInfoFormValidationSchema } from '../../../campaign-basic-info/schema/get-basic-info-form-validation.schema';
import { OuterVehiclePopulationFormPropsModel } from '../../models/outer-vehicle-population-form-props.model';
import { VehiclePopulationFormikModel } from '../../models/vehicle-population-formik.model';

export const useCreateNewCampaignOnSubmit = () => {
  return (
    values: VehiclePopulationFormikModel,
    formikBag: FormikBag<
      OuterVehiclePopulationFormPropsModel,
      VehiclePopulationFormikModel
    >
  ) => {
    const {
      name,
      startDate,
      endDate,
      wifiExpirationDate,
      fotaUpdateType,
      serverInitiatedType,
      safetyRelevant,
      maxRetryNo,
      is5GDownloadingAllowed,
      version,
      description,
      firmwareIds,
      status,
    } = formikBag.props.basicInfoStateFields;

    const basicInfoForm: BasicInfoRequestModel = {
      name,
      startDate: startDate?.toISOString() as string,
      endDate: endDate?.toISOString() as string,
      wifiExpirationDate: wifiExpirationDate?.toISOString() as string,
      fotaUpdateType: fotaUpdateType.toString(),
      serverInitiatedType: serverInitiatedType.toString(),
      safetyRelevant: safetyRelevant,
      maxRetryNo: maxRetryNo as number,
      is5GDownloadingAllowed: is5GDownloadingAllowed,
      version,
      description,
      firmwareIds,
      status: status?.label as string,
    };

    const {
      dispatch,
      navigate,
      isOnEdit,
      campaignId,
      snackbarCtx,
      snackBarConfig,
    } = formikBag.props;

    const vehiclePopulationForm: VehiclePopulationRequestModel = {
      vinCriteria: values.vinCriteria.filter(str => str.trim() !== ''),
    };

    const successSnackBarConfig = (value: number | CampaignResponseModel) => {
      snackBarConfig(
        'success',
        `Successfully ${
          typeof value !== 'number' ? 'updated' : 'created'
        } the campaign !`,
        <CheckmarkIcon />
      );
    };

    const errorSnackBarConfig = (errorMsg: string) => {
      snackBarConfig('error', errorMsg, <WarningIcon />);
    };

    try {
      getBasicInfoFormValidationSchema.validateSync({
        ...basicInfoForm,
        status,
      });

      const campaignThunkAction = isOnEdit ? updateCampaign : createCampaign;

      const executedThunkAction: AsyncThunkAction<
        CampaignResponseModel | number,
        CampaignRequestModel,
        Record<string, unknown>
      > = campaignThunkAction({
        ...(isOnEdit && campaignId && { id: campaignId }),
        ...basicInfoForm,
        ...vehiclePopulationForm,
      });

      dispatch(executedThunkAction)
        .unwrap()
        .then((value: CampaignResponseModel | number) => {
          dispatch(setBasicInfoError(false));
          dispatch(resetBasicInfo());
          dispatch(resetVehiclePopulation());
          successSnackBarConfig(value);
          const path = typeof value === 'number' ? `/${value}` : `/${value.id}`;
          if (typeof value === 'number') {
            navigate('../../campaigns');
          } else {
            navigate(
              `/campaign-management/edit${path}/reports?campaignCreated=1`,
              {
                replace: true,
              }
            );
          }
        })
        .catch(err => {
          let errorMsg =
            'Something went wrong! Please check basic information.';
          if (axios.isAxiosError(err)) {
            errorMsg = err.message;
          }
          errorSnackBarConfig(errorMsg);
        })
        .finally(() => snackbarCtx.isOpen(true));
    } catch (err) {
      dispatch(setBasicInfoError(true));
      errorSnackBarConfig(
        'Something went wrong! Please check basic information.'
      );
      snackbarCtx.isOpen(true);
    }
  };
};
